/* ==========================================================================
   HL — PRODUCT VIDEOS (block product_videos)

   Drives the carousel built by snippets/product-videos.liquid.

   What it coordinates:
     - native <video> (Shopify files, Dropbox, direct MP4)
     - Vimeo and YouTube embeds, over postMessage
   Only one clip plays at a time across both kinds, and everything stops when
   the carousel scrolls out of view.

   Swiper ships with the theme (assets/swiper-bundle.min.js, 11.2.10) so the
   JS and CSS can't drift apart the way the CDN copies did. The URL arrives on
   a data attribute because an asset here can't call asset_url.
   ========================================================================== */
(() => {
  const SECTION_SELECTOR = ".product-videos-section";
  const POSTER_FRAGMENT = "#t=0.1";
  const TAP_SLOP = 10;

  /* Only the host is rewritten. The signature on a modern /scl/fi/ link lives
     in st= and rlkey; appending raw=1 invalidates it and returns 403. Every
     other query param is left exactly as it arrived — the old version tried to
     strip "?dl=0" with a literal replace, which silently did nothing once the
     parameter showed up as "&dl=0". */
  const normalizeDropboxUrl = (url) => {
    if (!url) return "";
    const hash = url.indexOf("#");
    const base = hash === -1 ? url : url.slice(0, hash);
    const fragment = hash === -1 ? POSTER_FRAGMENT : url.slice(hash);
    if (!base.includes("dropbox.com")) return base + fragment;
    return base.replace("www.dropbox.com", "dl.dropboxusercontent.com") + fragment;
  };

  const ensureScript = (src, cb) => {
    if (window.Swiper) return cb();

    const waitForGlobal = () => {
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (window.Swiper) {
          clearInterval(timer);
          cb();
        } else if (tries > 100) {
          clearInterval(timer);
          console.warn("[product-videos] Swiper never became available");
        }
      }, 50);
    };

    /* Another component may already be loading the same file — wait for the
       global rather than racing a second copy into the page. */
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", cb, { once: true });
      waitForGlobal();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener("load", () => cb(), { once: true });
    script.addEventListener("error", () =>
      console.warn("[product-videos] could not load Swiper from", src)
    );
    document.body.appendChild(script);
  };

  /* Cross-origin players can't be reached through the DOM, but Vimeo and
     YouTube both accept commands over postMessage without their SDKs. */
  const frameCommand = (iframe, action) => {
    if (!iframe || !iframe.contentWindow) return;
    const wrapper = iframe.closest("[data-provider]");
    const provider = wrapper ? wrapper.dataset.provider : "";

    try {
      if (provider === "vimeo") {
        const send = (payload) =>
          iframe.contentWindow.postMessage(
            JSON.stringify(payload),
            "https://player.vimeo.com"
          );
        if (action === "play") send({ method: "play" });
        else if (action === "pause") send({ method: "pause" });
        else if (action === "unmute") {
          /* setMuted alone is ignored by older player builds, so the volume is
             set too — whichever the build understands wins. */
          send({ method: "setMuted", value: false });
          send({ method: "setVolume", value: 1 });
        } else if (action === "mute") {
          send({ method: "setMuted", value: true });
          send({ method: "setVolume", value: 0 });
        }
      } else if (provider === "youtube") {
        const send = (func, args) =>
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: func, args: args || [] }),
            "*"
          );
        if (action === "play") send("playVideo");
        else if (action === "pause") send("pauseVideo");
        else if (action === "unmute") {
          send("unMute");
          send("setVolume", [100]);
        } else if (action === "mute") send("mute");
      }
      /* Instagram exposes no such API; its embed just stays where it is. */
    } catch (err) {
      /* Frame not ready yet — the next interaction will retry. */
    }
  };

  /* An embed's state can't be read back across origins, so it is tracked
     here. Every command goes through these helpers so the buttons and the
     player never disagree. */
  const setPlayingState = (wrapper, playing) => {
    if (wrapper) wrapper.classList.toggle("is-playing", playing);
  };

  const setSoundState = (wrapper, muted) => {
    const button = wrapper && wrapper.querySelector(".pv-sound");
    if (button) button.setAttribute("aria-pressed", muted ? "false" : "true");
  };

  const initSection = (section) => {
    if (section.dataset.pvReady === "true") return;
    section.dataset.pvReady = "true";

    const videos = Array.from(section.querySelectorAll(".video-thumbnail video"));
    const frames = Array.from(section.querySelectorAll(".video-thumbnail iframe"));

    videos.forEach((video) => {
      const src = video.getAttribute("src");
      const fixed = normalizeDropboxUrl(src);
      if (fixed !== src) video.setAttribute("src", fixed);

      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "metadata";
      primePosterFrame(video);
    });

    /* One clip at a time, whichever kind of player started it. */
    const stopEverythingExcept = (keepVideo, keepFrame) => {
      videos.forEach((other) => {
        if (other !== keepVideo && !other.paused) {
          other.pause();
          other.currentTime = 0;
          other.muted = true;
          setSoundState(other.closest(".video-thumbnail"), true);
        }
      });
      frames.forEach((frame) => {
        if (frame === keepFrame) return;
        frameCommand(frame, "pause");
        frame.dataset.pvPlaying = "false";
        setPlayingState(frame.closest(".video-thumbnail"), false);
      });
    };

    /* Silence without stopping: used when one card claims the audio. */
    const muteEverythingExcept = (keepFrame, keepVideo) => {
      videos.forEach((other) => {
        if (other !== keepVideo && !other.muted) {
          other.muted = true;
          setSoundState(other.closest(".video-thumbnail"), true);
        }
      });
      frames.forEach((frame) => {
        if (frame !== keepFrame && frame.dataset.pvMuted !== "true") {
          frameCommand(frame, "mute");
          frame.dataset.pvMuted = "true";
          setSoundState(frame.closest(".video-thumbnail"), true);
        }
      });
    };

    /* A native video announces its own play, so the rule holds however
       playback started, and the badge follows the media rather than the
       click that triggered it. */
    videos.forEach((video) => {
      const wrapper = video.closest(".video-thumbnail");
      video.addEventListener("play", () => {
        stopEverythingExcept(video, null);
        setPlayingState(wrapper, true);
      });
      video.addEventListener("pause", () => setPlayingState(wrapper, false));
      video.addEventListener("ended", () => setPlayingState(wrapper, false));
    });

    /* Custom controls replace each provider's own chrome, so a Vimeo card and
       a Dropbox card behave identically. */
    section.querySelectorAll(".video-thumbnail").forEach((wrapper) => {
      const frame = wrapper.querySelector("iframe");
      const video = wrapper.querySelector("video");
      const hit = wrapper.querySelector(".pv-hit");
      const sound = wrapper.querySelector(".pv-sound");
      if (!hit) return;

      hit.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (frame) {
          const playing = frame.dataset.pvPlaying === "true";
          if (playing) {
            frameCommand(frame, "pause");
            frame.dataset.pvPlaying = "false";
            setPlayingState(wrapper, false);
          } else {
            stopEverythingExcept(null, frame);
            frameCommand(frame, "play");
            frame.dataset.pvPlaying = "true";
            setPlayingState(wrapper, true);
          }
          return;
        }

        if (!video) return;
        if (video.paused) playVideo(video);
        else video.pause();
      });

      if (!sound) return;
      sound.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (frame) {
          const muted = frame.dataset.pvMuted !== "false";
          if (muted) {
            muteEverythingExcept(frame, null);
            frameCommand(frame, "unmute");
            frame.dataset.pvMuted = "false";
            setSoundState(wrapper, false);
            if (frame.dataset.pvPlaying !== "true") {
              frameCommand(frame, "play");
              frame.dataset.pvPlaying = "true";
              setPlayingState(wrapper, true);
            }
          } else {
            frameCommand(frame, "mute");
            frame.dataset.pvMuted = "true";
            setSoundState(wrapper, true);
          }
          return;
        }

        if (!video) return;
        if (video.muted) {
          muteEverythingExcept(null, video);
          video.muted = false;
          setSoundState(wrapper, false);

          /* Only start it if it isn't already running. Calling play() on a
             playing element can reject with AbortError, and the old catch
             read that as "audio refused" and muted the clip straight back —
             which is why unmuting appeared to do nothing. */
          if (video.paused) {
            video.play().catch(() => {
              video.muted = true;
              setSoundState(wrapper, true);
              video.play().catch(() => {});
            });
          }
        } else {
          video.muted = true;
          setSoundState(wrapper, true);
        }
      });
    });

    /* Vimeo reports playback once asked to. Without this, starting a Vimeo
       clip left every other player running underneath it. */
    frames.forEach((frame) => {
      const wrapper = frame.closest("[data-provider]");
      if (!wrapper || wrapper.dataset.provider !== "vimeo") return;
      frame.addEventListener("load", () => {
        try {
          frame.contentWindow.postMessage(
            JSON.stringify({ method: "addEventListener", value: "play" }),
            "https://player.vimeo.com"
          );
        } catch (err) {
          /* Embed blocked by domain privacy; nothing to listen to. */
        }
      });
    });

    window.addEventListener("message", (event) => {
      if (!event.data || typeof event.data !== "string") return;
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (err) {
        return;
      }
      if (!payload || payload.event !== "play") return;

      const source = frames.find((frame) => frame.contentWindow === event.source);
      if (!source) return;
      stopEverythingExcept(null, source);
      source.dataset.pvPlaying = "true";
      setPlayingState(source.closest(".video-thumbnail"), true);
    });

    const swiperEl = section.querySelector(".swiper");
    const totalSlides = section.querySelectorAll(".swiper-slide").length;

    new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 20,
      observer: true,
      observeParents: true,
      grabCursor: totalSlides > 1,
      autoHeight: false,
      navigation:
        totalSlides > 1
          ? {
              nextEl: section.querySelector(".swiper-button-next"),
              prevEl: section.querySelector(".swiper-button-prev"),
            }
          : false,
      pagination:
        totalSlides > 1
          ? { el: section.querySelector(".swiper-pagination"), clickable: true }
          : false,
      breakpoints: {
        768: { slidesPerView: 2, spaceBetween: 24 },
        1024: { slidesPerView: 2, spaceBetween: 24 },
      },
      // Swiper swallows the click that fires at the end of a drag. With these
      // off, finishing a swipe on a slide also toggled that slide's video.
      preventClicks: true,
      preventClicksPropagation: true,
      touchStartPreventDefault: false,
      threshold: 6,
      // Let a mostly-vertical drag scroll the page instead of fighting the
      // carousel for it.
      touchAngle: 35,
      touchReleaseOnEdges: true,
      on: {
        transitionEnd() {
          /* On phones a slide is the whole viewport, so leaving one behind
             should stop it. Embeds included, which the old build never did. */
          if (window.innerWidth >= 768) return;
          const active = this.slides[this.activeIndex];
          videos.forEach((video) => {
            if (!active || !active.contains(video)) {
              video.pause();
              video.currentTime = 0;
              video.muted = true;
            }
          });
          frames.forEach((frame) => {
            if (active && active.contains(frame)) return;
            frameCommand(frame, "pause");
            frame.dataset.pvPlaying = "false";
            setPlayingState(frame.closest(".video-thumbnail"), false);
          });
        },
      },
    });

    // iOS loads metadata without decoding a frame, so nudge it to the fragment
    // time once dimensions are known. HAVE_CURRENT_DATA means a frame is
    // already painted and nothing is needed.
    function primePosterFrame(video) {
      const seek = () => {
        if (video.dataset.posterPrimed || video.readyState >= 2) return;
        video.dataset.posterPrimed = "true";
        try {
          video.currentTime = 0.1;
        } catch (err) {
          /* Some browsers refuse to seek before enough data arrives. */
        }
      };

      video.addEventListener("loadedmetadata", seek);
      if (video.readyState >= 1) seek();
    }

    // Audible playback is refused outside a trusted gesture and in Low Power
    // Mode; fall back to muted so the video still moves instead of staying black.
    function playVideo(video) {
      const wrapper = video.closest(".video-thumbnail");
      muteEverythingExcept(null, video);
      video.muted = false;
      setSoundState(wrapper, false);

      /* Audible playback is refused outside a trusted gesture and in Low Power
         Mode; fall back to muted so the clip still moves instead of freezing.
         The fallback only applies to a genuine rejection of this play call. */
      const attempt = video.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => {
          video.muted = true;
          setSoundState(wrapper, true);
          video.play().catch(() => {});
        });
      }
    }

    // A tap toggles playback, a swipe must not. Track how far the finger
    // travelled and ignore anything that moved, so ending a swipe on a slide
    // no longer plays or pauses it.
    section.querySelectorAll(".swiper-slide").forEach((slide) => {
      let startX = 0;
      let startY = 0;
      let dragged = false;

      slide.addEventListener(
        "touchstart",
        (e) => {
          const t = e.changedTouches[0];
          startX = t.clientX;
          startY = t.clientY;
          dragged = false;
        },
        { passive: true }
      );

      slide.addEventListener(
        "touchmove",
        (e) => {
          const t = e.changedTouches[0];
          if (
            Math.abs(t.clientX - startX) > TAP_SLOP ||
            Math.abs(t.clientY - startY) > TAP_SLOP
          ) {
            dragged = true;
          }
        },
        { passive: true }
      );

      slide.addEventListener("touchend", (e) => {
        if (dragged) return;
        handleTap(slide, e);
      });
    });

    /* Touch only. Clicks are handled by the .pv-hit layer, which sits above
       the media; handling both here would toggle playback twice per tap. */
    function handleTap(slide, e) {
      if (e.target.closest(".pv-sound")) return;
      const video = slide.querySelector("video");
      if (!video) return;

      e.preventDefault();
      e.stopPropagation();

      if (video.paused) playVideo(video);
      else video.pause();
    }

    /* The first card starts itself, muted, once the carousel is on screen —
       a silent moving clip is what tells people the rest are videos too. It
       backs off the moment someone picks a card of their own, so it never
       fights the visitor for control. */
    const firstCard = section.querySelector(".video-thumbnail");
    const autoplayFirst = () => {
      if (!firstCard || section.dataset.pvUserActed === "true") return;
      const video = firstCard.querySelector("video");
      const frame = firstCard.querySelector("iframe");
      if (video && video.paused) {
        video.muted = true;
        setSoundState(firstCard, true);
        video.play().catch(() => {});
      } else if (frame && frame.dataset.pvPlaying !== "true") {
        frameCommand(frame, "play");
        frame.dataset.pvPlaying = "true";
        setPlayingState(firstCard, true);
      }
    };

    section.addEventListener("click", (event) => {
      if (event.target.closest(".pv-hit, .pv-sound")) {
        section.dataset.pvUserActed = "true";
      }
    });

    /* Nothing should keep playing once the carousel is off screen. */
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              /* Embeds ignore commands sent before they finish booting. */
              window.setTimeout(autoplayFirst, 400);
              return;
            }
            videos.forEach((video) => {
              if (!video.paused) {
                video.pause();
                video.muted = true;
              }
            });
            frames.forEach((frame) => {
              frameCommand(frame, "pause");
              frame.dataset.pvPlaying = "false";
              setPlayingState(frame.closest(".video-thumbnail"), false);
            });
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(section);
    } else {
      window.setTimeout(autoplayFirst, 400);
    }
  };

  const bootstrap = () => {
    /* querySelectorAll, not querySelector: the old build only ever wired the
       first instance on the page. */
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sections.length) return;

    const swiperSrc =
      sections[0].dataset.swiperSrc ||
      "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";

    ensureScript(swiperSrc, () => sections.forEach(initSection));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }

  /* The theme editor re-renders a block into a fresh DOM node, which drops the
     Swiper instance and every listener with it. */
  document.addEventListener("shopify:section:load", (event) => {
    const sections = event.target.querySelectorAll
      ? Array.from(event.target.querySelectorAll(SECTION_SELECTOR))
      : [];
    if (!sections.length) return;
    sections.forEach((section) => {
      section.dataset.pvReady = "false";
    });
    ensureScript(sections[0].dataset.swiperSrc, () =>
      sections.forEach(initSection)
    );
  });
})();
